import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import QRCode from 'qrcode';
import '@shoelace-style/shoelace/dist/components/details/details.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/drawer/drawer.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';
import '../pending-sales-review';
import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../../mixins/with-active-shop-consumer';
import { WithRealtimeConsumer } from '../../mixins/with-realtime-consumer';
import { resolveRouterPath, router } from '../../router';
import { write } from '../../language/lang';
import { IShopAccess } from '../../api/model/login-access-token';
import {
  connectionStateStore,
  type ConnectionStateSnapshot,
} from '../../state/connection-state';
import {
  pendingSalesStore,
  type PendingSalesSnapshot,
} from '../../state/pending-sales-store';
import { themeStore, type ThemeChoice } from '../../theme/theme-store';
import { setUserContextMode } from '../../state/user-context-mode';
import { ApiFactory } from '../../api/easypos-api';
import type { Mode } from '../../datatypes/device';

// Sub-phase U.1 — the chrome that wraps every authenticated page.
//
// Responsibilities:
//   1. Decide breakpoint: phone (<720px) / tablet-portrait (720-1023) / desktop (≥1024).
//   2. Render the correct nav container:
//        phone               → top app bar (44px) + slide-in drawer
//        tablet-portrait,    → persistent left sidebar (default 240px,
//        desktop                collapsible to 64px rail via toolbar caret)
//   3. Host the router outlet in the main area.
//   4. Re-render on route change so the active nav item highlights correctly.
//
// What it deliberately does NOT do:
//   - Per-route bottom action bars (those belong to each page; U.3 will
//     standardise a slot pattern).
//   - Theme toggling (U.2).
//   - PWA-update banner (kept in app-index for now since it's app-level).
const Base = WithRealtimeConsumer(
  WithActiveShopConsumer(WithUserProfileConsumer(LitElement))
);

const PHONE_MAX = 719;
const TABLET_MAX = 1023;

@customElement('app-shell')
export class AppShell extends Base {
  @property({ type: String }) deviceMode: Mode = 'all-in-one';

  @state() private _viewport: 'phone' | 'tablet' | 'desktop' = 'desktop';
  @state() private _drawerOpen = false;
  @state() private _pathname = window.location.pathname;
  @state() private _themeChoice: ThemeChoice = themeStore.choice;

  private _mediaPhone = window.matchMedia(`(max-width: ${PHONE_MAX}px)`);
  private _mediaTablet = window.matchMedia(
    `(min-width: ${PHONE_MAX + 1}px) and (max-width: ${TABLET_MAX}px)`
  );

  // Bound listeners (so we can remove them).
  private _onMediaChange = () => this._recomputeViewport();
  private _onPopState = () => (this._pathname = window.location.pathname);

  private _unsubTheme: (() => void) | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this._recomputeViewport();
    this._mediaPhone.addEventListener('change', this._onMediaChange);
    this._mediaTablet.addEventListener('change', this._onMediaChange);
    window.addEventListener('popstate', this._onPopState);
    // Reflect theme-store changes so the toggle icon updates immediately.
    this._unsubTheme = themeStore.subscribe((_eff, choice) => {
      this._themeChoice = choice;
    });
    // Q.1 — keep the connection badge + offline banner reactive.
    this._connectionUnsub = connectionStateStore.subscribe((snap) => {
      this._connection = snap;
    });
    // Q.3 — pending-sales count for the banner.
    this._pendingSalesUnsub = pendingSalesStore.subscribe((snap) => {
      this._pendingSales = snap;
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._mediaPhone.removeEventListener('change', this._onMediaChange);
    this._mediaTablet.removeEventListener('change', this._onMediaChange);
    window.removeEventListener('popstate', this._onPopState);
    this._unsubTheme?.();
    this._unsubTheme = null;
    this._connectionUnsub?.();
    this._connectionUnsub = undefined;
    this._pendingSalesUnsub?.();
    this._pendingSalesUnsub = undefined;
  }

  private _recomputeViewport(): void {
    if (this._mediaPhone.matches) this._viewport = 'phone';
    else if (this._mediaTablet.matches) this._viewport = 'tablet';
    else this._viewport = 'desktop';
  }

  static styles = css`
    :host {
      display: grid;
      height: 100vh;
      overflow: hidden;
      background: var(--app-bg, var(--sl-color-neutral-50));
      color: var(--app-text, var(--sl-color-neutral-900));
      font-family: var(--sl-font-sans);
      --sidebar-width: 260px;
    }

    /* === DESKTOP / TABLET — persistent sidebar layout === */
    :host([data-viewport='desktop']),
    :host([data-viewport='tablet']) {
      grid-template-columns: var(--sidebar-width) 1fr;
      grid-template-rows: 1fr;
    }
    aside.sidebar {
      grid-column: 1;
      display: flex;
      flex-direction: column;
      width: var(--sidebar-width);
      border-right: 1px solid var(--app-border, var(--sl-color-neutral-200));
      background: var(--app-surface, white);
      overflow: hidden;
    }

    /* --- Sidebar header --- */
    /* The banner is filled with the app-icon's own navy (#0F2A44, the SVG's
       background rect) so the full-bleed logo melts into one continuous navy
       field rather than sitting as a small tile on white. Wordmark flips light
       for contrast on the navy in both themes. The icon SVG centres its artwork
       in an 80% maskable safe zone (lots of transparent margin); .logo-wrap is a
       fixed 80px viewport and the img is over-rendered to 120px so overflow:hidden
       crops that margin away and the mark fills the banner tightly. */
    .sidebar-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 16px;
      background: #0f2a44;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
      flex: none;
    }
    .sidebar-header .logo-wrap {
      width: 80px;
      height: 80px;
      flex: none;
      overflow: hidden;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .sidebar-header img.logo-mark {
      width: 120px;
      height: 120px;
      display: block;
    }
    .sidebar-header .brand {
      font-family: var(--font-display, inherit);
      font-size: 17px;
      font-weight: 800;
      letter-spacing: -0.02em;
      flex: 1;
      color: #e8edf2;
    }
    .sidebar-header .brand-accent {
      color: var(--brand-gold-400);
    }

    /* --- Accordion body (scrollable) --- */
    .sidebar-body {
      flex: 1 1 auto;
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
    }

    /* --- Accordion (sl-details) chrome --- */
    sl-details {
      display: block;
    }
    sl-details::part(base) {
      border: none;
      border-radius: 0;
      background: transparent;
    }
    sl-details::part(header) {
      padding: 7px 10px 7px 14px;
      border-bottom: none;
    }
    sl-details::part(summary) {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.07em;
      text-transform: uppercase;
      color: var(--app-text-muted);
    }
    sl-details::part(summary-icon) {
      font-size: 11px;
    }
    sl-details::part(content) {
      padding: 2px 0 6px;
    }
    .accordion-sep {
      height: 1px;
      background: var(--app-border);
      margin: 0;
    }

    /* --- Nav items inside accordions --- */
    .item {
      display: grid;
      grid-template-columns: 20px 1fr;
      align-items: center;
      gap: 10px;
      padding: 8px 14px 8px 20px;
      border-left: 3px solid transparent;
      color: var(--app-text);
      background: transparent;
      text-decoration: none;
      cursor: pointer;
      transition:
        background 80ms ease,
        border-color 80ms ease;
      font: 13.5px / 1.4 var(--sl-font-sans);
      text-align: left;
      border-top: none;
      border-right: none;
      border-bottom: none;
      width: 100%;
      box-sizing: border-box;
    }
    .item:hover {
      background: var(--sl-color-neutral-100);
    }
    :host-context(.sl-theme-dark) .item:hover {
      background: var(--sl-color-neutral-800);
    }
    .item.active {
      background: var(--sl-color-primary-50);
      border-left-color: var(--brand-gold-400);
      color: var(--sl-color-primary-700);
      font-weight: 600;
    }
    :host-context(.sl-theme-dark) .item.active {
      background: rgba(255, 255, 255, 0.06);
      border-left-color: var(--brand-gold-400);
      color: var(--brand-gold-300);
    }
    .item sl-icon {
      font-size: 16px;
      flex: none;
      color: currentColor;
    }
    .item-label {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .item-muted {
      color: var(--app-text-muted);
    }
    .item-danger {
      color: var(--sl-color-danger-600);
    }
    :host-context(.sl-theme-dark) .item-danger {
      color: var(--sl-color-danger-400);
    }

    /* --- Sidebar footer (connection status only) --- */
    .sidebar-footer {
      padding: 6px 14px;
      border-top: 1px solid var(--app-border, var(--sl-color-neutral-100));
      display: flex;
      align-items: center;
      gap: 6px;
      flex: none;
      min-height: 34px;
    }
    .connection-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      flex: none;
    }
    .connection-dot.online {
      background: var(--sl-color-success-500);
    }
    .connection-dot.offline {
      background: var(--sl-color-danger-500);
    }
    .connection-dot.reconnecting {
      background: var(--sl-color-warning-500);
    }
    .connection-label {
      font-size: 11px;
      color: var(--app-text-muted);
      flex: 1;
    }

    /* --- Connection + tier banners --- */
    .conn-banner {
      margin: 8px;
    }
    .conn-banner::part(base) {
      border-radius: var(--sl-border-radius-medium);
    }
    .conn-banner--action::part(base) {
      cursor: pointer;
    }

    main {
      grid-column: 2;
      overflow-y: auto;
      min-width: 0;
    }

    /* === PHONE — top app bar + drawer === */
    :host([data-viewport='phone']) {
      grid-template-columns: 1fr;
      grid-template-rows: 52px 1fr;
    }
    :host([data-viewport='phone']) aside.sidebar {
      display: none;
    }
    .top-app-bar {
      grid-row: 1;
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 0 6px;
      background: var(--brand-navy-900);
      color: #fff;
      border-bottom: none;
      box-shadow: var(--app-shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.2));
      position: sticky;
      top: 0;
      z-index: 5;
    }
    .top-app-bar sl-icon-button::part(base) {
      color: #fff;
    }
    .top-app-bar .shop-pill {
      flex: 1;
      padding: 0 6px;
      font-family: var(--font-display, inherit);
      font-size: 16px;
      font-weight: 700;
      letter-spacing: -0.01em;
      color: #fff;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .top-app-bar .top-app-bar-right {
      display: flex;
      align-items: center;
      gap: 4px;
      padding-right: 10px;
    }
    .top-app-bar .connection-dot {
      box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.18);
    }
    :host([data-viewport='phone']) main {
      grid-column: 1;
      grid-row: 2;
    }

    /* Drawer */
    sl-drawer::part(panel) {
      --size: min(92vw, 320px);
    }
    .drawer-body {
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: hidden;
    }
    .drawer-nav {
      flex: 1;
      overflow-y: auto;
      overscroll-behavior: contain;
    }
    .drawer-footer {
      padding: 6px 14px;
      border-top: 1px solid var(--app-border);
      display: flex;
      align-items: center;
      gap: 6px;
      min-height: 34px;
      flex: none;
    }
  `;

  protected updated(changedProps: Map<string | symbol, unknown>) {
    super.updated(changedProps);
    this.setAttribute('data-viewport', this._viewport);
    if (changedProps.has('activeShop')) {
      this._qrCode = null;
      this._qrSvg = null;
    }
  }

  private _shops(): IShopAccess[] {
    return (this.userProfile?.shopAccess ?? []).filter((s) => s.active);
  }

  private _switchToCustomer() {
    setUserContextMode('customer');
    router.navigate(resolveRouterPath('customer'));
    this._pathname = window.location.pathname;
    if (this._viewport === 'phone') this._drawerOpen = false;
  }

  @state() private _qrDialogOpen = false;
  @state() private _qrSvg: string | null = null;
  @state() private _qrCode: string | null = null;
  @state() private _qrLoading = false;

  @state() private _qrJoinUrl: string | null = null;

  private async _openQrDialog() {
    const shopName = this.activeShop?.shopName;
    if (!shopName) return;
    this._qrDialogOpen = true;
    if (this._qrCode) return; // already loaded for this shop
    this._qrLoading = true;
    const r = await ApiFactory.authenticated().getShop(shopName);
    if (r.success && r.data?.publicShopCode) {
      this._qrCode = r.data.publicShopCode;
      // Encode a deep link to the join page so a scanned QR takes the
      // customer straight to the "Join {shop}" flow (logging in first if
      // they need to). The code text is still rendered separately so a
      // customer can also type it manually.
      this._qrJoinUrl = `${window.location.origin}${resolveRouterPath('join/')}${this._qrCode}`;
      this._qrSvg = await QRCode.toString(this._qrJoinUrl, {
        type: 'svg',
        margin: 2,
        width: 240,
        color: { dark: '#0f2a44', light: '#ffffff' },
      });
    }
    this._qrLoading = false;
  }

  private async _printQr() {
    if (!this._qrSvg || !this._qrCode) return;
    const shopName =
      this.activeShop?.shopDisplayName || this.activeShop?.shopName || '';
    const w = window.open('', '_blank', 'width=560,height=820');
    if (!w) return;
    // Clean, modern counter-card. White background, generous whitespace,
    // tight type. Reads as a contemporary shop printable — not retro.
    w.document.write(`<!doctype html>
<html><head><title>Join ${shopName} — Pag-asenso</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400..700&display=swap">
<style>
  :root {
    --ink: #0f172a;
    --ink-soft: #475569;
    --ink-faint: #94a3b8;
    --line: #e2e8f0;
    --accent: #e1477e;
  }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #f1f5f9; }
  body {
    font-family: 'Bricolage Grotesque', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    color: var(--ink);
    padding: 32px;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    min-height: 100vh;
    -webkit-font-smoothing: antialiased;
  }
  .card {
    width: 380px;
    background: #ffffff;
    border: 1px solid var(--line);
    border-radius: 16px;
    padding: 40px 36px 28px;
    text-align: left;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }
  .eyebrow {
    display: inline-block;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--accent);
    margin-bottom: 12px;
  }
  h1 {
    font-size: 30px;
    font-weight: 700;
    line-height: 1.1;
    letter-spacing: -0.025em;
    margin: 0 0 8px;
    color: var(--ink);
  }
  .shop-name {
    color: var(--ink);
  }
  .lede {
    font-size: 14px;
    color: var(--ink-soft);
    margin: 0 0 28px;
    line-height: 1.5;
  }
  .qr-frame {
    width: 100%;
    aspect-ratio: 1;
    padding: 14px;
    background: white;
    border: 1px solid var(--line);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .qr-frame svg { width: 100%; height: 100%; display: block; }

  .row {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-top: 20px;
    padding: 14px 16px;
    border: 1px solid var(--line);
    border-radius: 12px;
  }
  .row-label {
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--ink-faint);
    flex: none;
  }
  .row-divider {
    width: 1px;
    align-self: stretch;
    background: var(--line);
    flex: none;
  }
  .code {
    flex: 1;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: 0.16em;
    font-feature-settings: 'tnum';
    color: var(--ink);
    text-align: right;
    line-height: 1;
  }

  .steps {
    margin: 24px 0 0;
    padding: 0;
    list-style: none;
    counter-reset: step;
  }
  .steps li {
    counter-increment: step;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 8px 0;
    font-size: 13px;
    color: var(--ink-soft);
    line-height: 1.45;
  }
  .steps li::before {
    content: counter(step);
    flex: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--ink);
    color: white;
    font-size: 11px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1px;
  }
  .steps b { color: var(--ink); font-weight: 600; }

  .brand-foot {
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid var(--line);
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11.5px;
    color: var(--ink-faint);
    letter-spacing: 0.02em;
  }
  .brand-foot strong {
    color: var(--ink);
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  @media print {
    @page { margin: 0.5in; }
    body { background: white; padding: 0; }
    .card { border: 1px solid var(--line); box-shadow: none; }
  }
</style>
</head>
<body>
  <div class="card">
    <div class="eyebrow">Loyalty programme</div>
    <h1>Join <span class="shop-name">${shopName}</span></h1>
    <p class="lede">Scan to become a member and start earning rewards on every visit.</p>

    <div class="qr-frame">${this._qrSvg}</div>

    <div class="row">
      <span class="row-label">Code</span>
      <span class="row-divider"></span>
      <span class="code">${this._qrCode}</span>
    </div>

    <ol class="steps">
      <li>Open <b>Pag-asenso</b> on your phone, or scan with the camera.</li>
      <li>Tap <b>Customer view</b>, then <b>Join a shop</b>.</li>
      <li>Enter the code above and you're in.</li>
    </ol>

    <div class="brand-foot">
      <span><strong>Pag-asenso</strong></span>
      <span>pag-asenso.app</span>
    </div>
  </div>
  <script>window.onload = () => { setTimeout(() => window.print(), 300); };</script>
</body></html>`);
    w.document.close();
  }

  private _renderQrDialog() {
    return html`
      <sl-dialog
        class="full-page-on-phone"
        label="Shop QR code"
        ?open=${this._qrDialogOpen}
        @sl-after-hide=${() => (this._qrDialogOpen = false)}
        style="--width: 360px;"
      >
        <div
          style="display:flex;flex-direction:column;align-items:center;gap:1rem;padding:0.5rem 0;"
        >
          ${
            this._qrLoading
              ? html`<sl-spinner style="font-size:3rem;"></sl-spinner>`
              : this._qrSvg
                ? html`
                    <div
                      style="border-radius:12px;overflow:hidden;border:1px solid var(--app-border);"
                    >
                      ${unsafeHTML(this._qrSvg)}
                    </div>
                    <div
                      style="font-size:1.5rem;font-weight:800;letter-spacing:0.15em;font-variant-numeric:tabular-nums;color:var(--app-text);"
                    >
                      ${this._qrCode}
                    </div>
                    <p
                      style="margin:0;font-size:0.82rem;color:var(--app-text-muted);text-align:center;max-width:280px;"
                    >
                      Print this QR for the counter — scanning takes customers
                      to the join page. Or share the code so they can enter it
                      manually.
                    </p>
                  `
                : html`<p
                    style="color:var(--app-text-muted);font-size:0.875rem;"
                  >
                    Could not load shop code.
                  </p>`
          }
        </div>
        <sl-button
          slot="footer"
          variant="primary"
          @click=${this._printQr}
          ?disabled=${!this._qrSvg}
        >
          <sl-icon slot="prefix" name="printer"></sl-icon>
          Print
        </sl-button>
        <sl-button slot="footer" @click=${() => (this._qrDialogOpen = false)}
          >Close</sl-button
        >
      </sl-dialog>
    `;
  }

  // Q.1 — single connection-state source. The badge + offline banner
  // both read this snapshot; the subscription keeps it fresh.
  @state() private _connection: ConnectionStateSnapshot =
    connectionStateStore.snapshot;
  private _connectionUnsub?: () => void;

  // Q.3 — pending sales (offline-queued, awaiting drain). The banner
  // surfaces the count so the cashier sees how much hasn't synced.
  @state() private _pendingSales: PendingSalesSnapshot =
    pendingSalesStore.snapshot;
  private _pendingSalesUnsub?: () => void;

  // Q.3 follow-up — the failed-sales review dialog (server-rejected offline
  // sales the operator must retry or discard).
  @state() private _showFailedReview = false;

  private _connectionClass(): 'online' | 'offline' | 'reconnecting' {
    return this._connection.state;
  }

  private _connectionLabel(): string {
    if (this._connection.realtimeDisabled) return 'Online (realtime off)';
    switch (this._connection.state) {
      case 'online':
        return 'Online';
      case 'reconnecting':
        return 'Reconnecting…';
      case 'offline':
        return 'Offline';
    }
  }

  private _logout() {
    this.dispatchEvent(
      new CustomEvent('logout', { bubbles: true, composed: true })
    );
    if (this._viewport === 'phone') this._drawerOpen = false;
  }

  // Active-route matcher. Empty `to` (home) needs exact match; stock has
  // a custom matcher so /stocktake doesn't light up Stock; stocktake uses
  // a prefix match. Everything else is exact-or-prefix.
  private _isActive(to: string): boolean {
    const href = resolveRouterPath(to);
    const p = this._pathname;
    if (to === '') return p === href || p === '/' || p === '';
    if (to === 'stock')
      return (
        p === href ||
        (p.startsWith(href + '/') &&
          !p.startsWith(resolveRouterPath('stocktake')))
      );
    if (to === 'stocktake') return p.startsWith(href);
    return p === href || p.startsWith(href + '/');
  }

  private _navTo(to: string) {
    router.navigate(resolveRouterPath(to));
    this._pathname = window.location.pathname;
    if (this._viewport === 'phone') this._drawerOpen = false;
  }

  private _navLink(to: string, icon: string, label: string) {
    const href = resolveRouterPath(to);
    const active = this._isActive(to);
    return html`
      <a
        class="item ${active ? 'active' : ''}"
        href=${href}
        @click=${(e: Event) => {
          e.preventDefault();
          this._navTo(to);
        }}
      >
        <sl-icon name=${icon}></sl-icon>
        <span class="item-label">${label}</span>
      </a>
    `;
  }

  private _navAction(
    action: () => void,
    icon: string,
    label: string,
    extraClass = ''
  ) {
    return html`
      <button
        class="item ${extraClass}"
        @click=${() => {
          action();
          if (this._viewport === 'phone') this._drawerOpen = false;
        }}
      >
        <sl-icon name=${icon}></sl-icon>
        <span class="item-label">${label}</span>
      </button>
    `;
  }

  // "Me" accordion — user-scoped items independent of any shop.
  private _renderMeAccordion() {
    const name = this.userProfile?.displayName ?? 'My account';
    const customerLabel = this.userProfile?.isCustomer
      ? 'Loyalty memberships'
      : 'Customer view';
    return html`
      <sl-details summary=${name}>
        ${this._navLink('user/profile', 'person-circle', 'My profile')}
        ${this._navLink('help', 'question-circle', 'Help')}
        ${this._navLink('about', 'info-circle', 'About')}
        ${this._navAction(() => this._switchToCustomer(), 'heart-fill', customerLabel)}
        ${this._navAction(() => themeStore.cycle(), themeIconFor(this._themeChoice), themeLabelFor(this._themeChoice), 'item-muted')}
        ${this._navAction(() => this._logout(), 'box-arrow-right', 'Sign out', 'item-danger')}
      </sl-details>
    `;
  }

  // Active-shop accordion — operational items for the current shop.
  private _renderShopAccordion() {
    const active = this.activeShop;
    if (!active) return nothing;
    const isOwner = active.roles.includes('owner');
    const mode = this.deviceMode;
    const summary = active.shopDisplayName || active.shopName;
    const items: ReturnType<typeof html>[] = [];

    const showAllOps =
      mode === 'all-in-one' || mode === 'manager' || mode === 'display';
    // Optional Inventory/Procurement module. When off (every current tier),
    // hide the stock / stocktake / suppliers / POs / deliveries surfaces.
    // See ShopFeatures.AllowsInventory.
    const allowsInventory = active.subscription?.allowsInventory === true;
    // B1 — Cash management module. When on, surface the Cash drawer item
    // in the Sell section.
    const allowsCashManagement =
      active.subscription?.allowsCashManagement === true;

    // Loyalty QR always sits at the top of the shop nav — any staff
    // member at the counter should be able to surface it instantly
    // when a customer asks about loyalty.
    items.push(
      this._navAction(
        () => void this._openQrDialog(),
        'qr-code',
        'Loyalty QR code'
      )
    );

    if (showAllOps && isOwner) {
      items.push(this._navLink('', 'house-door', 'Home'));
      items.push(this._navLink('dashboard', 'speedometer2', 'Dashboard'));
    }

    if (mode === 'kitchen') {
      items.push(this._navLink('orders/active', 'list-task', 'Kitchen board'));
    } else if (mode === 'stock') {
      // Stock-mode device but inventory off → degenerate (tier shouldn't
      // grant stock mode without inventory). Skip the stock items so the
      // operator isn't pointed at a hidden surface.
      if (allowsInventory) {
        items.push(this._navLink('stock', 'box-seam', 'Stock items'));
        items.push(
          this._navLink('stocktake', 'clipboard-check', 'Stock takes')
        );
        items.push(this._navLink('suppliers', 'truck', 'Suppliers'));
        items.push(
          this._navLink('purchase-orders', 'receipt', 'Purchase orders')
        );
        items.push(
          this._navLink('deliveries', 'box-arrow-in-down', 'Deliveries')
        );
      }
    } else {
      // pos / all-in-one / manager / display
      items.push(this._navLink('sales/new', 'cart-plus', 'Sell'));
      items.push(this._navLink('orders/active', 'list-task', 'Active orders'));
      items.push(
        this._navLink('sales/history', 'clock-history', 'Sales history')
      );
      if (allowsCashManagement) {
        items.push(this._navLink('cash', 'cash-stack', 'Cash drawer'));
        items.push(
          this._navLink('cash/sessions', 'journal-text', 'Cash sessions')
        );
      }

      if (showAllOps && isOwner) {
        items.push(this._navLink('products', 'card-list', 'Products'));
        items.push(this._navLink('categories', 'collection', 'Categories'));
        if (allowsInventory) {
          items.push(this._navLink('stock', 'box-seam', 'Stock items'));
          items.push(
            this._navLink('stocktake', 'clipboard-check', 'Stock takes')
          );
          items.push(this._navLink('suppliers', 'truck', 'Suppliers'));
          items.push(
            this._navLink('purchase-orders', 'receipt', 'Purchase orders')
          );
          items.push(
            this._navLink('deliveries', 'box-arrow-in-down', 'Deliveries')
          );
        }
        items.push(this._navLink('sales/reports', 'graph-up', 'Reports'));
      }
    }

    return html`
      <div class="accordion-sep"></div>
      <sl-details summary=${summary} open> ${items} </sl-details>
    `;
  }

  // "Shop admin" accordion — shop-management actions (settings, QR, switch, create).
  private _renderShopAdminAccordion() {
    const shops = this._shops();
    const active = this.activeShop;
    if (!active) return nothing;
    return html`
      <div class="accordion-sep"></div>
      <sl-details summary="Shop admin">
        ${this._navLink('shop/settings', 'gear', 'Shop settings')}
        ${
          // N.7.4 - staff live under Shop admin rather than the operating menus: adding people
          // is an owner's setup task, not something a cashier does mid-shift.
          this._navLink('staff', 'people', 'Staff')
        }
        ${shops.length > 1 ? this._navLink('shop/select', 'arrow-left-right', 'Switch shop') : nothing}
        ${this._navLink('shop/create', 'plus-circle', 'Create another shop')}
      </sl-details>
    `;
  }

  private _renderAccordions() {
    return html`
      ${this._renderMeAccordion()} ${this._renderShopAccordion()}
      ${this._renderShopAdminAccordion()}
    `;
  }

  private _renderSidebar() {
    return html`
      <aside class="sidebar">
        <div class="sidebar-header">
          <span class="logo-wrap"
            ><img
              class="logo-mark"
              src="/assets/icons/pag-asenso-icon.svg"
              alt="Pag-asenso"
          /></span>
          <div class="brand">Pag<span class="brand-accent">-asenso</span></div>
        </div>
        <div class="sidebar-body">${this._renderAccordions()}</div>
        <div class="sidebar-footer">
          <span
            class="connection-dot ${this._connectionClass()}"
            title=${this._connectionLabel()}
          ></span>
          <span class="connection-label">${this._connectionLabel()}</span>
        </div>
      </aside>
    `;
  }

  private _renderTopAppBar() {
    const active = this.activeShop;
    return html`
      <header class="top-app-bar">
        <sl-icon-button
          name="list"
          label="Open menu"
          @click=${() => (this._drawerOpen = true)}
          style="font-size: 22px;"
        ></sl-icon-button>
        <span class="shop-pill">
          ${active ? active.shopDisplayName || active.shopName : write`Pag-asenso`}
        </span>
        <div class="top-app-bar-right">
          <span
            class="connection-dot ${this._connectionClass()}"
            title=${this._connectionLabel()}
          ></span>
        </div>
      </header>
    `;
  }

  private _renderDrawer() {
    return html`
      <sl-drawer
        placement="start"
        label=${write`Menu`}
        ?open=${this._drawerOpen}
        @sl-after-hide=${() => (this._drawerOpen = false)}
      >
        <div class="drawer-body">
          <div class="drawer-nav">${this._renderAccordions()}</div>
          <div class="drawer-footer">
            <span
              class="connection-dot ${this._connectionClass()}"
              title=${this._connectionLabel()}
            ></span>
            <span class="connection-label">${this._connectionLabel()}</span>
          </div>
        </div>
      </sl-drawer>
    `;
  }

  render() {
    return html`
      ${
        this._viewport === 'phone'
          ? html`${this._renderTopAppBar()} ${this._renderDrawer()}`
          : this._renderSidebar()
      }
      <main>
        ${this._renderTierBlockedBanner()} ${this._renderFailedSalesBanner()}
        ${this._renderConnectionBanner()}
        <slot></slot>
      </main>
      ${this._renderQrDialog()}
      <pending-sales-review
        .open=${this._showFailedReview}
        @close=${() => (this._showFailedReview = false)}
      ></pending-sales-review>
    `;
  }

  // Q.3 follow-up — distinct from the "syncing" banner: sales the server
  // REJECTED on replay need an operator decision, so this is danger-styled and
  // tappable straight into the review dialog. Shows regardless of connection
  // state (you can be back online with a sale still stuck).
  private _renderFailedSalesBanner() {
    const failed = this._pendingSales.failed.length;
    if (failed === 0) return nothing;
    return html`
      <sl-alert
        variant="danger"
        open
        class="conn-banner conn-banner--action"
        @click=${() => (this._showFailedReview = true)}
      >
        <sl-icon slot="icon" name="exclamation-octagon"></sl-icon>
        ${failed} sale${failed === 1 ? '' : 's'} could not sync and need
        attention. Tap to review.
      </sl-alert>
    `;
  }

  // N.7a — separate banner explaining why live sync is off when the
  // shop's tier doesn't unlock realtime. Rendered as a sibling of the
  // Q.1+Q.3 banner so both can show concurrently (e.g. free-tier shop
  // ALSO has queued offline sales). Amber styling; capitalises the
  // tier name in the upsell copy.
  private _renderTierBlockedBanner() {
    const tb = this._connection.tierBlocked;
    if (!tb) return nothing;
    const required = tb.requiredTier
      ? tb.requiredTier.charAt(0).toUpperCase() + tb.requiredTier.slice(1)
      : 'Bronze';
    return html`
      <sl-alert variant="warning" open class="conn-banner">
        <sl-icon slot="icon" name="arrow-up-circle"></sl-icon>
        Live sync is a ${required} tier feature. ${tb.message}
      </sl-alert>
    `;
  }

  // Q.1 + Q.3 — banner pinned to the top of <main> when the app is in
  // a degraded connection state OR when sales are still queued in the
  // offline outbox. Doesn't change behaviour; explains it.
  private _renderConnectionBanner() {
    const s = this._connection.state;
    const pending = this._pendingSales.count;
    if (s === 'online' && pending === 0) return nothing;
    const variant =
      s === 'reconnecting' ? 'warning' : s === 'offline' ? 'danger' : 'primary'; // online but pending: informational
    const icon =
      s === 'reconnecting'
        ? 'arrow-clockwise'
        : s === 'offline'
          ? 'wifi-off'
          : 'cloud-upload';
    let label: string;
    if (s === 'offline') {
      label =
        pending > 0
          ? `Offline — ${pending} sale${pending === 1 ? '' : 's'} queued locally. They sync automatically when you reconnect.`
          : 'Offline — the app is using the last known state. Sales + edits will sync when you reconnect.';
    } else if (s === 'reconnecting') {
      label =
        pending > 0
          ? `Reconnecting… ${pending} sale${pending === 1 ? '' : 's'} pending sync.`
          : 'Reconnecting to the server… some features may be slow.';
    } else {
      // online + pending: drainer is in flight or about to be.
      label = `${pending} sale${pending === 1 ? '' : 's'} syncing…`;
    }
    return html`
      <sl-alert variant=${variant} open class="conn-banner">
        <sl-icon slot="icon" name=${icon}></sl-icon>
        ${label}
      </sl-alert>
    `;
  }
}

// Theme toggle icon set — bootstrap-icon names. auto = "sync" because
// the effective theme follows OS preference; light = sun; dark = moon.
function themeIconFor(choice: ThemeChoice): string {
  if (choice === 'light') return 'sun';
  if (choice === 'dark') return 'moon';
  return 'circle-half';
}

function themeLabelFor(choice: ThemeChoice): string {
  if (choice === 'light') return 'Theme: light (click for dark)';
  if (choice === 'dark') return 'Theme: dark (click for auto)';
  return 'Theme: auto (click for light)';
}

declare global {
  interface HTMLElementTagNameMap {
    'app-shell': AppShell;
  }
}
